// Terminology per business category (ERP rule 7, business handoff §2).
// Source of truth: design-system/locales.json `terminology`. Screens never
// hard-code المراجع/الزبون or الزيارة/الموعد — they call t('customer').
//
// Extensions (not in locales.json, which stays a byte-identical copy of the
// design package's design-system/locales.json): the plural rail/tab label
// `customers` (business handoff §3: «الزبائن» → «المراجعون» for clinics) and
// the DERIVED forms the boards draw on top of the definite singulars —
// feminine, indefinite, accusative, the plural column label, «آخر زيارة»,
// «زيارة أولى», the service-step noun, Arabic count agreement and the staff
// noun. They are keyed by the same term profile and live here, the one owner
// of terminology; feature folders read them, never copy them.
//
// Sector profiles (locales.json `_sector_terms_2026_10_15`): the `hotel`
// profile (sector «hotel») carries the board-27 words — النزيل · الإقامة ·
// الحجز · الغرفة · الفوليو · تسجيل الوصول · تسجيل المغادرة · التدبير المنزلي ·
// إغلاق اليوم · العقار (the property noun has a default, «الفرع»). Its customer
// family reads as guests (HANDOFF §17 «المراجعون→النزلاء»): the rail «النزلاء»
// and the forms the artboards draw («النزيل», «نزيل»); forms no artboard draws
// fall back to the default profile rather than being invented here.

import locales from './locales.json';
import { arNumber } from './format';

/** Definite singulars from locales.json + the documented extensions below. */
export type TermKey =
  | 'customer'
  | 'appointment'
  | 'walk_in'
  | 'business'
  /** Hotel (locales.json 2026-10-15): النزيل · الإقامة · الحجز · الغرفة · الفوليو · تسجيل الوصول · تسجيل المغادرة · التدبير المنزلي · إغلاق اليوم · العقار. */
  | 'guest'
  | 'stay'
  | 'reservation'
  | 'room'
  | 'folio'
  | 'check_in'
  | 'check_out'
  | 'housekeeping'
  | 'night_audit'
  | 'property'
  /** The property switcher's words (board 27): عقاراتي · عقار (إضافة عقار) · كل العقارات — derived from `property`, default فروعي · فرع · كل الفروع. */
  | 'properties_mine'
  | 'property_indef'
  | 'properties_all'
  | 'customers'
  /** المراجعة / الزبونة / العميلة (definite, feminine). */
  | 'customer_f'
  /** مراجع / زبون / عميل (indefinite) — «مراجع جديد». */
  | 'customer_indef'
  /** مراجعاً / زبوناً / عميلاً — «أضفه مراجعاً جديداً». */
  | 'customer_acc'
  /** مراجعةً / زبونةً / عميلةً — «أضفها مراجعةً جديدة». */
  | 'customer_acc_f'
  /** الزيارات / المواعيد (column header, stat label, tab). */
  | 'appointments'
  /** آخر زيارة / آخر موعد. */
  | 'last_appointment'
  /** زيارة أولى / موعد أول (zero visits). */
  | 'first_appointment'
  /** The booking panel's service step: clinic «الزيارة», otherwise «الخدمة» (boards). */
  | 'service_step';
export type TermProfile = 'default' | 'clinic' | 'gulf' | 'hotel';
export type Lang = 'ar' | 'en' | 'ckb';

type TermTable = Record<string, Record<string, Partial<Record<Lang, string>>>>;

type TermEntry = Record<string, Partial<Record<Lang, string>>>;

/** locales.json `terminology` without its documentation members («_sector_terms_2026_10_15»). */
const LOCALE_TERMS: TermTable = Object.fromEntries(
  Object.entries(locales.terminology as Record<string, unknown>).filter((e): e is [string, TermEntry] => typeof e[1] === 'object' && e[1] !== null),
);

const TERMS: TermTable = {
  ...LOCALE_TERMS,
  // The hotel's customer is its guest: the locales.json `guest` word, never retyped (board 27 «النزيل»).
  customer: { ...LOCALE_TERMS.customer, hotel: LOCALE_TERMS.guest.hotel },
  customers: {
    default: { ar: 'الزبائن', en: 'Customers' },
    clinic: { ar: 'المراجعون', en: 'Patients' },
    gulf: { ar: 'العملاء' },
    hotel: { ar: 'النزلاء', en: 'Guests' },
  },
  properties_mine: { default: { ar: 'فروعي', en: 'My branches' }, hotel: { ar: 'عقاراتي', en: 'My properties' } },
  property_indef: { default: { ar: 'فرع', en: 'branch' }, hotel: { ar: 'عقار', en: 'property' } },
  properties_all: { default: { ar: 'كل الفروع', en: 'All branches' }, hotel: { ar: 'كل العقارات', en: 'All properties' } },
  customer_f: { default: { ar: 'الزبونة', en: 'Customer' }, clinic: { ar: 'المراجعة', en: 'Patient' }, gulf: { ar: 'العميلة' } },
  customer_indef: { default: { ar: 'زبون', en: 'customer' }, clinic: { ar: 'مراجع', en: 'patient' }, gulf: { ar: 'عميل' }, hotel: { ar: 'نزيل', en: 'guest' } },
  customer_acc: { default: { ar: 'زبوناً', en: 'customer' }, clinic: { ar: 'مراجعاً', en: 'patient' }, gulf: { ar: 'عميلاً' } },
  customer_acc_f: { default: { ar: 'زبونةً', en: 'customer' }, clinic: { ar: 'مراجعةً', en: 'patient' }, gulf: { ar: 'عميلةً' } },
  appointments: { default: { ar: 'المواعيد', en: 'Appointments' }, clinic: { ar: 'الزيارات', en: 'Visits' } },
  last_appointment: { default: { ar: 'آخر موعد', en: 'Last appointment' }, clinic: { ar: 'آخر زيارة', en: 'Last visit' } },
  first_appointment: { default: { ar: 'موعد أول', en: 'First appointment' }, clinic: { ar: 'زيارة أولى', en: 'First visit' } },
  service_step: { default: { ar: 'الخدمة', en: 'Service' }, clinic: { ar: 'الزيارة', en: 'Visit' } },
};

const CLINIC_SECTORS = new Set(['clinic', 'dental', 'medical', 'vet', 'clinics', 'عيادة']);
const HOTEL_SECTORS = new Set(['hotel', 'hotels', 'فندق']);
const GULF_COUNTRIES = new Set(['SA', 'AE', 'KW', 'QA', 'BH', 'OM']);

/** Which terminology profile a business uses: sector wins (hotel, clinic), then country (gulf). */
export function termProfile(sector?: string | null, country?: string | null): TermProfile {
  if (sector && HOTEL_SECTORS.has(sector.toLowerCase())) return 'hotel';
  if (sector && CLINIC_SECTORS.has(sector.toLowerCase())) return 'clinic';
  if (country && GULF_COUNTRIES.has(country.toUpperCase())) return 'gulf';
  return 'default';
}

/** Resolve a term: profile → default profile, then lang → ar. */
export function term(key: TermKey, profile: TermProfile = 'default', lang: Lang = 'ar'): string {
  const entry = TERMS[key];
  if (!entry) return key;
  return entry[profile]?.[lang] ?? entry.default?.[lang] ?? entry[profile]?.ar ?? entry.default?.ar ?? key;
}

/** Curried translator bound to a business: const t = makeT('clinic'); t('customer') → 'المراجع'. */
export function makeT(profile: TermProfile, lang: Lang = 'ar'): (key: TermKey) => string {
  return (key) => term(key, profile, lang);
}

// ------------------------------------------------------- count agreement --

/** Arabic count agreement: 1 → «… واحد», 2 → dual, 3–10 → plural, 11+ → singular accusative. */
export interface CountForms {
  one: string;
  two: string;
  /** 3–10, after the number. */
  few: string;
  /** 11+, after the number. */
  many: string;
}

export type CountKey = 'customer' | 'appointment';

const COUNTS: Record<CountKey, Partial<Record<TermProfile, CountForms>>> = {
  customer: {
    default: { one: 'زبون واحد', two: 'زبونان', few: 'زبائن', many: 'زبوناً' },
    clinic: { one: 'مراجع واحد', two: 'مراجعان', few: 'مراجعين', many: 'مراجعاً' },
    gulf: { one: 'عميل واحد', two: 'عميلان', few: 'عملاء', many: 'عميلاً' },
  },
  appointment: {
    default: { one: 'موعد واحد', two: 'موعدان', few: 'مواعيد', many: 'موعداً' },
    clinic: { one: 'زيارة واحدة', two: 'زيارتان', few: 'زيارات', many: 'زيارة' },
  },
};

/** The count forms of a term for a profile (falls back to the default profile). */
export function countForms(key: CountKey, profile: TermProfile = 'default'): CountForms {
  const entry = COUNTS[key];
  return (entry[profile] ?? entry.default) as CountForms;
}

/** «٦ مراجعين» / «مراجع واحد» / «مراجعان» / «١١ مراجعاً». n is an API figure. */
export function countLabel(n: number, forms: CountForms): string {
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  const mod = n % 100;
  if (n >= 3 && mod >= 3 && mod <= 10) return `${arNumber(n)} ${forms.few}`;
  return `${arNumber(n)} ${forms.many}`;
}

// ------------------------------------------------------------ staff noun --

export interface StaffNoun {
  /** «الطبيب» / «المصففة» / «الموظف». */
  label: string;
  feminine: boolean;
}

const SALON_SECTORS = new Set(['salon', 'beauty', 'beauty_center', 'spa']);

/**
 * The staff noun (board booking/new--staff-pick draws «المصففة» for a salon;
 * spec O24 — not in locales.json): clinic «الطبيب», salon «المصففة»,
 * everything else «الموظف».
 */
export function staffNoun(profile: TermProfile, sector?: string | null): StaffNoun {
  if (profile === 'clinic') return { label: 'الطبيب', feminine: false };
  if (sector && SALON_SECTORS.has(sector.toLowerCase())) return { label: 'المصففة', feminine: true };
  return { label: 'الموظف', feminine: false };
}

// ----------------------------------------------------------- place nouns --

/**
 * The shape-2 place noun (board 22/25: «ملعب، مكان غسل، رافعة، جهاز، استوديو،
 * غرفة»). locales.json has no place nouns, so they are a documented extension
 * here keyed by CATEGORY id (ruling R6), with a generic default. The R6 triple
 * is indefinite («إضافة ملعب», «ملعب ٤», «٤ ملاعب», «كم ملعباً لديك؟»); the
 * definite pair serves the label and the sub («الملاعب», «الزبون لا يختار الملعب»).
 */
export interface PlaceTerms {
  /** ملعب (indefinite singular). */
  placeSing: string;
  /** ملاعب (indefinite plural, after a number 3–10). */
  placePlural: string;
  /** ملعباً (accusative indefinite, after كم). */
  placeAcc: string;
  /** ملعبان (the dual, a count of 2 — coordinator ruling). */
  placeDual: string;
  /** الملعب (definite singular). */
  placeSingDef: string;
  /** الملاعب (definite plural — the shape step's label). */
  placePluralDef: string;
}

const PLACES: Record<string, PlaceTerms> = {
  playground: { placeSing: 'ملعب', placePlural: 'ملاعب', placeAcc: 'ملعباً', placeDual: 'ملعبان', placeSingDef: 'الملعب', placePluralDef: 'الملاعب' },
  car_wash: { placeSing: 'مكان غسل', placePlural: 'أماكن غسل', placeAcc: 'مكان غسل', placeDual: 'مكانا غسل', placeSingDef: 'مكان الغسل', placePluralDef: 'أماكن الغسل' },
};

const DEFAULT_PLACE: PlaceTerms = { placeSing: 'مكان', placePlural: 'أماكن', placeAcc: 'مكاناً', placeDual: 'مكانان', placeSingDef: 'المكان', placePluralDef: 'الأماكن' };

/** The place nouns of a category (generic «مكان» when the category has none). */
export function placeTerms(categoryId?: string | null): PlaceTerms {
  return (categoryId && PLACES[categoryId]) || DEFAULT_PLACE;
}
